import apiDashboard from "services/apiDashboard";
import dayjs from "dayjs";
import { capitalizeFirstLetter } from "utils";

export const getCurrentMonthReport = async () => {
  try {
    const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
    const endDate = dayjs().format("YYYY-MM-DD");
    const { data: allData } = await apiDashboard.get(
      `api/v1/municipios/data?start=${startDate}&end=${endDate}`
    );
    const allMonths = [
      "janeiro",
      "fevereiro",
      "março",
      "abril",
      "maio",
      "junho",
      "julho",
      "agosto",
      "setembro",
      "outubro",
      "novembro",
      "dezembro",
    ];

    const currentMonthIndex = dayjs().month();
    const validMonths = allMonths.slice(0, currentMonthIndex + 1);

    const monthsData = allData.reduce((acc, city) => {
      city.data.forEach((item) => {
        if (item.b_status_text !== "CANCELADO") {
          const month = dayjs(item.registration_date)
            .format("MMMM")
            .toLowerCase();
          if (!acc[month]) {
            acc[month] = 0;
          }
          acc[month] += 1;
        }
      });
      return acc;
    }, {});

    const result = validMonths.map((month) => ({
      month: capitalizeFirstLetter(month),
      total: monthsData[month] || 0,
    }));

    const total = result.reduce((sum, item) => sum + item.total, 0);
    result.push({ month: "Total", total });

    return result;
  } catch (err) {
    console.error(err);
  }
};

export async function getEvolutionByPeriod() {
  // Relatório evolucao por Período
  // https://dev.orion.ufal.br/cria/cria_web_v2/-/issues/314?show=1844
  let data = new Date();
  let mesText = capitalizeFirstLetter(dayjs(new Date()).format("MMMM"));
  let ano = data.getFullYear();
  let anop = ano - 1;

  const startLastYear = `${anop}-01-01`;
  const endLastYear = `${anop}-12-31`;
  const startThisYear = `${ano}-01-01`;
  const endThisYear = dayjs().format("YYYY-MM-DD");

  try {
    // Execute both requests in parallel

    const [{ data: dataLastYear }, { data: dataThisYear }] = await Promise.all([
      apiDashboard.get(
        `api/v1/municipios/data?start=${startLastYear}&end=${endLastYear}&b_type='1'&b_status='1','2','3','4'`
      ),
      apiDashboard.get(
        `api/v1/municipios/data?start=${startThisYear}&end=${endThisYear}&b_type='1'&b_status='1','2','3','4'`
      ),
    ]);

    const totalLastYear = dataLastYear.reduce((total, city) => {
      const validDataCount = city.data.filter(
        (item) => item.b_status_text !== "CANCELADO"
      ).length;
      return total + validDataCount;
    }, 0);

    const totalThisYear = dataThisYear.reduce((total, city) => {
      const validDataCount = city.data.filter(
        (item) => item.b_status_text !== "CANCELADO"
      ).length;
      return total + validDataCount;
    }, 0);

    return [
      {
        year: anop,
        text: "Janeiro à Dezembro",
        total: totalLastYear,
      },
      {
        year: ano,
        text: `Janeiro à ${mesText}`,
        total: totalThisYear,
      },
    ];
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}

export async function getCitiesRanking() {
  const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  try {
    const { data } = await apiDashboard.get(
      `api/v1/municipios/data?start=${startDate}&end=${endDate}&b_type='1'&b_status='1','2','3','4'`
    );

    const group = data.reduce((r, a) => {
      const validDataCount = a.data.filter(
        (item) => item.b_status_text !== "CANCELADO"
      ).length;
      r[a.city] = (r[a.city] || 0) + validDataCount;
      return r;
    }, {});

    const top10Cities = Object.entries(group)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([city, total]) => ({ city, total }));

    return top10Cities;
  } catch (err) {
    console.error(err);
  }
}

export async function getSituations() {
  const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  const { data } = await apiDashboard.get(
    `api/v1/municipios/data?start=${startDate}&end=${endDate}&b_type='1','2','3','4','5'&b_status='1','2','3','4'&traditional_population_groups='101','201','202','203','204','205','301','302','303','304','305','306','0'`
  );

  const withoutCancelados = data
    .flatMap((item) => item.data)
    .filter((obj) => obj.b_status_text !== "CANCELADO");

  const groupedDataBeneficiaryType = withoutCancelados.reduce((acc, obj) => {
    const type = obj.b_type_text;
    if (type) {
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, {});

  const totalBeneficiaryType = Object.values(groupedDataBeneficiaryType).reduce(
    (sum, value) => sum + value,
    0
  );

  const formattedTableBeneficiaryType = Object.entries(
    groupedDataBeneficiaryType
  ).map(([type, quantity]) => ({
    type: type,
    total: quantity,
  }));

  formattedTableBeneficiaryType.push({
    type: "TOTAL",
    total: totalBeneficiaryType,
  });

  const groupedDataFamilyType = withoutCancelados.reduce((acc, obj) => {
    const type = obj.traditional_population_groups_text;
    if (type) {
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, {});

  const formattedTableFamilyType = Object.entries(groupedDataFamilyType).map(
    ([type, quantity]) => ({
      type: type,
      total: quantity,
    })
  );

  const formattedData = {
    familiesType: formattedTableFamilyType,
    beneficiariesType: formattedTableBeneficiaryType,
  };
  return formattedData;
}

export async function getDemographic() {
  const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  const { data } = await apiDashboard.get(
    `api/v1/municipios/data?start=${startDate}&end=${endDate}&race='1','2','3','4','5'&gender_text='FEMININO','MASCULINO'&education_level='1','2','3','4','5','6'`
  );

  const groupBy = (items, key) => {
    return items.reduce((acc, obj) => {
      const type = obj[key];
      if (type) {
        acc[type] = (acc[type] || 0) + 1;
      }
      return acc;
    }, {});
  };

  const formatGroupedData = (groupedData, header) => {
    const total = Object.values(groupedData).reduce(
      (sum, value) => sum + value,
      0
    );
    const formatted = Object.entries(groupedData).map(([type, quantity]) => [
      type,
      quantity,
    ]);
    formatted.push(["TOTAL", total]);
    return [header, ...formatted];
  };

  const withoutCancelados = data.flatMap((item) =>
    item.data.filter((obj) => obj.b_status_text !== "CANCELADO")
  );

  const groupedByState = {
    gendersType: formatGroupedData(groupBy(withoutCancelados, "gender_text"), [
      "Gênero",
      "Quantitativo",
    ]),
    racesType: formatGroupedData(groupBy(withoutCancelados, "race_text"), [
      "Raça",
      "Quantitativo",
    ]),
    educationLevelsType: formatGroupedData(
      groupBy(withoutCancelados, "education_level_text"),
      ["Instrução", "Quantitativo"]
    ),
  };

  const groupedByCity = data.map((cityData) => {
    const cityName = cityData.city;
    const cityDataFiltered = cityData.data.filter(
      (obj) => obj.b_status_text !== "CANCELADO"
    );

    return {
      city: cityName,
      gendersType: formatGroupedData(groupBy(cityDataFiltered, "gender_text"), [
        "Gênero",
        "Quantitativo",
      ]),
      racesType: formatGroupedData(groupBy(cityDataFiltered, "race_text"), [
        "Raça",
        "Quantitativo",
      ]),
      educationLevelsType: formatGroupedData(
        groupBy(cityDataFiltered, "education_level_text"),
        ["Instrução", "Quantitativo"]
      ),
    };
  });

  return {
    state: groupedByState,
    cities: groupedByCity,
  };
}

export async function getPoverty() {
  const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  const { data } = await apiDashboard.get(
    `api/v1/municipios/data?start=${startDate}&end=${endDate}&b_type='2','3','4'&linha_pobreza='EXTREMA POBREZA','POBREZA'`
  );

  const withoutGestantes = data.flatMap((item) =>
    item.data.filter(
      (obj) =>
        obj.b_status_text !== "CANCELADO" &&
        obj.b_type_text !== "GESTANTE" &&
        obj.child_birth &&
        obj.linha_pobreza
    )
  );

  const countByAgeRangeAndPoverty = (items, povertyLine) => {
    const childrenInPoverty = items.filter(
      (item) => item.linha_pobreza === povertyLine
    );

    const total0To4 = childrenInPoverty.filter((item) => {
      const age =
        new Date().getFullYear() - new Date(item.child_birth).getFullYear();
      return age >= 0 && age <= 4;
    }).length;

    const total5To6 = childrenInPoverty.filter((item) => {
      const age =
        new Date().getFullYear() - new Date(item.child_birth).getFullYear();
      return age >= 5 && age <= 6;
    }).length;

    const total = total0To4 + total5To6;

    return {
      total0To4,
      total5To6,
      total,
    };
  };

  const extremaPobreza = countByAgeRangeAndPoverty(
    withoutGestantes,
    "EXTREMA POBREZA"
  );
  const pobreza = countByAgeRangeAndPoverty(withoutGestantes, "POBREZA");

  return {
    extremaPobreza: [
      ["Faixa Etária", "TOTAL"],
      ["Entre 0 e 4 anos", extremaPobreza.total0To4],
      ["Entre 5 a 6 anos", extremaPobreza.total5To6],
      ["Total", extremaPobreza.total],
    ],
    pobreza: [
      ["Faixa Etária", "TOTAL"],
      ["Entre 0 e 4 anos", pobreza.total0To4],
      ["Entre 5 a 6 anos", pobreza.total5To6],
      ["Total", pobreza.total],
    ],
  };
}

export async function getCartaoCria() {
  const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  try {
    const { data: allData } = await apiDashboard.get(
      `api/v1/municipios/data?start=${startDate}&end=${endDate}`
    );
    const groupedByCity = allData.reduce((acc, item) => {
      if (!acc[item.city]) {
        acc[item.city] = [];
      }
      acc[item.city] = acc[item.city].concat(item.data);
      return acc;
    }, {});

    const countByAgeRangeAndPoverty = (items, povertyLine) => {
      const filteredItems = items.filter(
        (obj) =>
          obj.b_status_text !== "CANCELADO" &&
          obj.b_type_text !== "GESTANTE" &&
          obj.child_birth &&
          obj.linha_pobreza
      );

      const childrenInPoverty = filteredItems.filter(
        (item) => item.linha_pobreza === povertyLine
      );

      const total0To4 = childrenInPoverty.filter((item) => {
        const age =
          new Date().getFullYear() - new Date(item.child_birth).getFullYear();
        return age >= 0 && age <= 4;
      }).length;

      const total5To6 = childrenInPoverty.filter((item) => {
        const age =
          new Date().getFullYear() - new Date(item.child_birth).getFullYear();
        return age >= 5 && age <= 6;
      }).length;

      const total = total0To4 + total5To6;

      return {
        total0To4,
        total5To6,
        total,
      };
    };

    const result = Object.entries(groupedByCity).map(([city, items]) => {
      const extremaPobreza = countByAgeRangeAndPoverty(
        items,
        "EXTREMA POBREZA"
      );
      const pobreza = countByAgeRangeAndPoverty(items, "POBREZA");

      const gestantes = items.filter(
        (item) => item.b_status_text !== "CANCELADO" && item.b_type === "1"
      ).length;
      const criancasComZica = items.filter(
        (item) => item.b_status_text !== "CANCELADO" && item.b_type === "2"
      ).length;
      const beneficiariosInativos = items.filter(
        (item) => item.b_status_text === "CANCELADO"
      ).length;
      const criancasAte6Anos = items.filter(
        (item) => item.b_status_text !== "CANCELADO" && item.b_type === "3"
      ).length;
      const gemeos = items.filter(
        (item) => item.b_status_text !== "CANCELADO" && item.b_type === "4"
      ).length;

      const totalCriancas0a6 = extremaPobreza.total + pobreza.total;

      return {
        city,
        extremaPobreza: [
          ["Faixa Etária", "TOTAL"],
          ["Entre 0 e 4 anos", extremaPobreza.total0To4],
          ["Entre 5 a 6 anos", extremaPobreza.total5To6],
          ["Total", extremaPobreza.total],
        ],
        pobreza: [
          ["Faixa Etária", "TOTAL"],
          ["Entre 0 e 4 anos", pobreza.total0To4],
          ["Entre 5 a 6 anos", pobreza.total5To6],
          ["Total", pobreza.total],
        ],
        additionalInfo: {
          totalCriancas0a6,
          beneficiariosAtivos: items.length - beneficiariosInativos,
          beneficiariosInativos,
          gestantes,
          criancasComZica,
          criancasAte6Anos,
          gemeos,
        },
      };
    });

    return result;
  } catch (err) {
    console.error(err);
  }
}
